import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Bookmark, BookmarkPlus, Trash2, FileDown, RefreshCw } from "lucide-react";
import {
  useCaseList, useAcknowledge, useSavedSearches, useSaveSearch, useDeleteSavedSearch,
  useSyncStatus, useTriggerSync,
  type CaseFilters,
} from "../../lib/queries";
import { useAuth } from "../../lib/auth";
import { SectionTag, StatCard, Button } from "../../components/ui";
import ExportMenu from "../../components/ExportMenu";
import { useToast } from "../../components/ui/Toast";
import { fmtNum, fmtTime } from "../../lib/format";
import type { EventRow } from "../../types/api";
import CasesFilters from "./CasesFilters";
import CasesTable from "./CasesTable";
import ExceptionDetail from "../exception/ExceptionDetail";

const LIMIT = 25;
const FILTER_KEYS = ["severity", "status", "event_type", "site", "asset", "media_type", "has_media", "dead_man", "min_risk", "search", "ordering", "offset"];

function readFilters(sp: URLSearchParams): CaseFilters {
  return {
    severity: sp.getAll("severity"),
    status: sp.getAll("status"),
    event_type: sp.getAll("event_type").map(Number),
    site: sp.get("site") ? Number(sp.get("site")) : "",
    asset: sp.get("asset") ? Number(sp.get("asset")) : "",
    media_type: sp.get("media_type") ?? undefined,
    has_media: sp.get("has_media") === "true",
    dead_man: sp.get("dead_man") === "true",
    min_risk: sp.get("min_risk") ? Number(sp.get("min_risk")) : undefined,
    search: sp.get("search") ?? undefined,
    ordering: sp.get("ordering") ?? "-start_time",
    offset: sp.get("offset") ? Number(sp.get("offset")) : 0,
    limit: LIMIT,
  };
}

function writeFilters(sp: URLSearchParams, f: CaseFilters) {
  FILTER_KEYS.forEach((k) => sp.delete(k));
  (f.severity ?? []).forEach((v) => sp.append("severity", v));
  (f.status ?? []).forEach((v) => sp.append("status", v));
  (f.event_type ?? []).forEach((v) => sp.append("event_type", String(v)));
  if (f.site) sp.set("site", String(f.site));
  if (f.asset) sp.set("asset", String(f.asset));
  if (f.media_type) sp.set("media_type", f.media_type);
  if (f.has_media) sp.set("has_media", "true");
  if (f.dead_man) sp.set("dead_man", "true");
  if (f.min_risk) sp.set("min_risk", String(f.min_risk));
  if (f.search) sp.set("search", f.search);
  if (f.ordering && f.ordering !== "-start_time") sp.set("ordering", f.ordering);
  if (f.offset) sp.set("offset", String(f.offset));
}

/** Facets sent to the async bulk export (date range is merged by ExportMenu). */
function exportFilters(f: CaseFilters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.severity?.length) out.severity = f.severity;
  if (f.status?.length) out.status = f.status;
  if (f.event_type?.length) out.event_type = f.event_type;
  if (f.site) out.site = f.site;
  if (f.asset) out.asset = f.asset;
  if (f.media_type) out.media_type = f.media_type;
  if (f.has_media) out.has_media = true;
  if (f.dead_man) out.dead_man = true;
  if (f.min_risk) out.min_risk = f.min_risk;
  if (f.search) out.search = f.search;
  return out;
}

function pageCsv(rows: EventRow[]) {
  const head = ["case", "time", "type", "emesrt", "severity", "risk", "status", "site", "asset", "machine", "media", "channel"];
  const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const body = rows.map((r) => [
    r.ext_id, fmtTime(r.start_time), r.event_type_label, r.emesrt_level, r.severity,
    r.risk_score, r.status, r.site_title, r.asset_registration, r.machine_type,
    r.media?.type, r.channel,
  ].map(esc).join(","));
  const csv = [head.join(","), ...body].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = "cases-page.csv";
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function Cases() {
  const [sp, setSp] = useSearchParams();
  const { capabilities } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const canTriage = capabilities.includes("events.triage");
  const canSync = capabilities.includes("config.write");

  const syncStatus = useSyncStatus();
  const triggerSync = useTriggerSync();
  const syncing = !!syncStatus.data?.running || triggerSync.isPending;

  // When a sync finishes (running flips true -> false), refresh the register.
  const wasSyncing = useRef(false);
  useEffect(() => {
    const running = !!syncStatus.data?.running;
    if (wasSyncing.current && !running) {
      const run = syncStatus.data?.latest_run;
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["monitor-summary"] });
      if (run?.status === "success") {
        const inc = run.stats?.incidents as { created?: number; updated?: number } | undefined;
        toast.success(`Sync complete — ${inc?.created ?? 0} new, ${inc?.updated ?? 0} refreshed.`);
      } else if (run) {
        toast.error(`Sync finished with status “${run.status}”${run.error ? ` — ${run.error}` : ""}.`);
      }
    }
    wasSyncing.current = running;
  }, [syncStatus.data, qc, toast]);

  function startSync() {
    triggerSync.mutate(undefined, {
      onSuccess: () => toast.success("Sync started — pulling latest data from SafetyShieldVue."),
      onError: (e) => {
        const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        toast.error(detail ?? "Could not start the sync.");
      },
    });
  }

  const filters = useMemo(() => readFilters(sp), [sp]);
  const selectedCase = sp.get("case") ? Number(sp.get("case")) : null;

  const { data, isLoading, isFetching } = useCaseList(filters);
  const rows: EventRow[] = data?.results ?? [];
  const count = data?.count ?? 0;

  // Cheap headline pivots (count-only queries respecting the current filters).
  const severe = useCaseList({ ...filters, severity: ["high", "critical"], offset: 0, limit: 1 });
  const withMedia = useCaseList({ ...filters, has_media: true, offset: 0, limit: 1 });

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const ack = useAcknowledge();
  const savedSearches = useSavedSearches();
  const saveSearch = useSaveSearch();
  const deleteSaved = useDeleteSavedSearch();

  function commit(f: CaseFilters, resetOffset = true) {
    const next = new URLSearchParams(sp);
    writeFilters(next, { ...f, offset: resetOffset ? 0 : f.offset });
    setSp(next);
    setSelected(new Set());
  }
  const patch = (part: Partial<CaseFilters>) => commit({ ...filters, ...part });
  function setOffset(offset: number) {
    const next = new URLSearchParams(sp);
    next.set("offset", String(offset));
    setSp(next);
  }
  function sortBy(field: string) {
    const cur = filters.ordering;
    const next = cur === field ? `-${field}` : cur === `-${field}` ? "-start_time" : field;
    patch({ ordering: next });
  }
  function clearAll() {
    const next = new URLSearchParams();
    const c = sp.get("case");
    if (c) next.set("case", c);
    setSp(next);
    setSelected(new Set());
  }
  function openCase(id: number) { const n = new URLSearchParams(sp); n.set("case", String(id)); setSp(n); }
  function closeCase() { const n = new URLSearchParams(sp); n.delete("case"); setSp(n); }

  function toggle(id: number) {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleAll() {
    setSelected((s) => (s.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));
  }
  async function bulkAck() {
    const ids = [...selected];
    await Promise.all(ids.map((id) => ack.mutateAsync(id).catch(() => null)));
    toast.success(`Acknowledged ${ids.length} case${ids.length === 1 ? "" : "s"}.`);
    setSelected(new Set());
  }

  function saveCurrentView() {
    const name = window.prompt("Save this view as…");
    if (!name) return;
    saveSearch.mutate(
      { name, facets: exportFilters(filters) as Record<string, unknown> },
      { onSuccess: () => toast.success(`Saved view “${name}”.`) }
    );
  }
  function applyView(facets: Record<string, unknown>) {
    // facets stored the export shape; map back into CaseFilters.
    commit({
      severity: (facets.severity as string[]) ?? [],
      status: (facets.status as string[]) ?? [],
      event_type: (facets.event_type as number[]) ?? [],
      site: (facets.site as number) ?? "",
      asset: (facets.asset as number) ?? "",
      media_type: facets.media_type as string | undefined,
      has_media: !!facets.has_media,
      dead_man: !!facets.dead_man,
      min_risk: facets.min_risk as number | undefined,
      search: facets.search as string | undefined,
      ordering: "-start_time",
      limit: LIMIT,
    });
  }

  return (
    <div className="space-y-4">
      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <SectionTag>CASES · EXCEPTION REGISTER</SectionTag>
          <h1 className="text-xl font-semibold">All Cases</h1>
          <p className="text-sm text-slatey">
            Every exception across the tenant — filter anything, drill into any case, export the
            exact view. {isFetching && <span className="text-aurora-cyan">· updating…</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canSync && syncStatus.data?.configured && (
            <Button variant="ghost" onClick={startSync} disabled={syncing}>
              <span className="inline-flex items-center gap-1.5"
                title="Pull the latest data from SafetyShieldVue">
                <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
                {syncing ? "Syncing…" : "Sync"}
              </span>
            </Button>
          )}
          <Button variant="ghost" onClick={() => pageCsv(rows)}>
            <span className="inline-flex items-center gap-1.5"><FileDown size={14} /> Page CSV</span>
          </Button>
          <ExportMenu title="Export all" extraFilters={exportFilters(filters)} />
        </div>
      </div>

      {/* stat strip */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="TOTAL CASES" value={fmtNum(count)} accent="var(--aurora-cyan)" />
        <StatCard label="HIGH & CRITICAL" value={fmtNum(severe.data?.count ?? 0)} critical
          sub={<button className="text-aurora-cyan hover:underline" onClick={() => patch({ severity: ["high", "critical"] })}>filter →</button>} />
        <StatCard label="WITH MEDIA" value={fmtNum(withMedia.data?.count ?? 0)} accent="var(--aurora-teal)"
          sub={<button className="text-aurora-cyan hover:underline" onClick={() => patch({ has_media: true })}>filter →</button>} />
        <StatCard label="ON THIS PAGE" value={`${rows.length} / ${LIMIT}`} />
      </div>

      {/* saved views */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="section-tag inline-flex items-center gap-1"><Bookmark size={12} /> SAVED VIEWS</span>
        {(savedSearches.data ?? []).map((v) => (
          <span key={v.id} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 py-0.5 pl-2.5 pr-1 text-xs">
            <button onClick={() => applyView(v.facets)} className="text-slate-300 hover:text-ink">{v.name}</button>
            <button onClick={() => deleteSaved.mutate(v.id)} title="Delete" className="rounded-full p-0.5 text-slatey hover:text-critical"><Trash2 size={11} /></button>
          </span>
        ))}
        <button onClick={saveCurrentView} className="inline-flex items-center gap-1 rounded-full border border-dashed border-white/15 px-2.5 py-0.5 text-xs text-slatey hover:border-aurora-cyan/40 hover:text-ink">
          <BookmarkPlus size={12} /> Save current
        </button>
      </div>

      <CasesFilters filters={filters} onChange={patch} onClear={clearAll} />

      <CasesTable
        rows={rows}
        count={count}
        isLoading={isLoading}
        ordering={filters.ordering ?? "-start_time"}
        onSort={sortBy}
        offset={filters.offset ?? 0}
        limit={LIMIT}
        onOffset={setOffset}
        onOpen={openCase}
        selected={selected}
        onToggle={toggle}
        onToggleAll={toggleAll}
        canTriage={canTriage}
        onBulkAck={bulkAck}
        bulkPending={ack.isPending}
      />

      <ExceptionDetail eventId={selectedCase} onClose={closeCase} />
    </div>
  );
}
